import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Trash2, Upload, Eye, EyeOff, Images, Loader2, ImageIcon, X } from "lucide-react";

interface Album {
  id: string;
  name: string;
  description: string | null;
  cover_url: string | null;
  is_published: boolean;
  created_at: string | null;
}

interface Photo {
  id: string;
  photo_url: string;
  display_order: number | null;
  album_id: string;
}

export const GalleryTab = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedAlbum, setSelectedAlbum] = useState<Album | null>(null);
  const [newAlbumName, setNewAlbumName] = useState("");
  const [newAlbumDescription, setNewAlbumDescription] = useState("");
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);

  // Fetch all albums
  const { data: albums, isLoading: loadingAlbums } = useQuery({
    queryKey: ["admin-albums"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("photo_albums")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as Album[];
    },
  });

  // Fetch photos for selected album
  const { data: photos, isLoading: loadingPhotos } = useQuery({
    queryKey: ["admin-album-photos", selectedAlbum?.id],
    queryFn: async () => {
      if (!selectedAlbum) return [];
      const { data, error } = await supabase
        .from("album_photos")
        .select("*")
        .eq("album_id", selectedAlbum.id)
        .order("display_order", { ascending: true });
      if (error) throw error;
      return data as Photo[];
    },
    enabled: !!selectedAlbum,
  });

  // Create album mutation
  const createAlbumMutation = useMutation({
    mutationFn: async () => {
      const { data: user } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from("photo_albums")
        .insert({
          name: newAlbumName,
          description: newAlbumDescription || null,
          created_by: user.user?.id,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-albums"] });
      setShowCreateDialog(false);
      setNewAlbumName("");
      setNewAlbumDescription("");
      toast({ title: "Álbum criado com sucesso!" });
    },
    onError: (error: any) => {
      toast({ title: "Erro ao criar álbum", description: error.message, variant: "destructive" });
    },
  });

  // Toggle publish mutation
  const togglePublishMutation = useMutation({
    mutationFn: async ({ id, isPublished }: { id: string; isPublished: boolean }) => {
      const { error } = await supabase
        .from("photo_albums")
        .update({ is_published: isPublished })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-albums"] });
      toast({ title: "Álbum atualizado!" });
    },
    onError: (error: any) => {
      toast({ title: "Erro ao atualizar álbum", description: error.message, variant: "destructive" });
    },
  });

  // Delete album mutation
  const deleteAlbumMutation = useMutation({
    mutationFn: async (id: string) => {
      // First delete all photos
      const { data: photos } = await supabase
        .from("album_photos")
        .select("photo_url")
        .eq("album_id", id);
      
      if (photos && photos.length > 0) {
        const paths = photos.map(p => {
          const url = new URL(p.photo_url);
          return url.pathname.split('/photo-albums/')[1];
        }).filter(Boolean);
        
        if (paths.length > 0) {
          await supabase.storage.from("photo-albums").remove(paths);
        }
      }
      
      const { error } = await supabase
        .from("photo_albums")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-albums"] });
      setSelectedAlbum(null);
      toast({ title: "Álbum excluído com sucesso!" });
    },
    onError: (error: any) => {
      toast({ title: "Erro ao excluir álbum", description: error.message, variant: "destructive" });
    },
  });

  // Delete photo mutation
  const deletePhotoMutation = useMutation({
    mutationFn: async (photo: Photo) => {
      // Delete from storage
      const url = new URL(photo.photo_url);
      const path = url.pathname.split('/photo-albums/')[1];
      if (path) {
        await supabase.storage.from("photo-albums").remove([path]);
      }
      
      const { error } = await supabase
        .from("album_photos")
        .delete()
        .eq("id", photo.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-album-photos", selectedAlbum?.id] });
      toast({ title: "Foto excluída!" });
    },
    onError: (error: any) => {
      toast({ title: "Erro ao excluir foto", description: error.message, variant: "destructive" });
    },
  });

  // Upload photos handler
  const handleUploadPhotos = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedAlbum || !e.target.files || e.target.files.length === 0) return;
    
    setUploadingPhotos(true);
    const files = Array.from(e.target.files);
    
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileExt = file.name.split(".").pop();
        const fileName = `${selectedAlbum.id}/${Date.now()}-${i}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from("photo-albums")
          .upload(fileName, file);
        
        if (uploadError) throw uploadError;
        
        const { data: { publicUrl } } = supabase.storage
          .from("photo-albums")
          .getPublicUrl(fileName);
        
        const { error: insertError } = await supabase
          .from("album_photos")
          .insert({
            album_id: selectedAlbum.id,
            photo_url: publicUrl,
            display_order: (photos?.length || 0) + i,
          });
        
        if (insertError) throw insertError;
      }
      
      queryClient.invalidateQueries({ queryKey: ["admin-album-photos", selectedAlbum.id] });
      toast({ title: `${files.length} foto(s) enviada(s) com sucesso!` });
    } catch (error: any) {
      toast({ title: "Erro ao enviar fotos", description: error.message, variant: "destructive" });
    } finally {
      setUploadingPhotos(false);
      e.target.value = "";
    }
  };

  // Upload cover handler
  const handleUploadCover = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedAlbum || !e.target.files || e.target.files.length === 0) return;
    
    setUploadingCover(true);
    const file = e.target.files[0];
    
    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `covers/${selectedAlbum.id}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from("photo-albums")
        .upload(fileName, file, { upsert: true });
      
      if (uploadError) throw uploadError;
      
      const { data: { publicUrl } } = supabase.storage
        .from("photo-albums")
        .getPublicUrl(fileName);
      
      const { error: updateError } = await supabase
        .from("photo_albums")
        .update({ cover_url: publicUrl + "?t=" + Date.now() })
        .eq("id", selectedAlbum.id);
      
      if (updateError) throw updateError;
      
      queryClient.invalidateQueries({ queryKey: ["admin-albums"] });
      setSelectedAlbum(prev => prev ? { ...prev, cover_url: publicUrl + "?t=" + Date.now() } : null);
      toast({ title: "Capa atualizada!" });
    } catch (error: any) {
      toast({ title: "Erro ao atualizar capa", description: error.message, variant: "destructive" });
    } finally {
      setUploadingCover(false);
      e.target.value = "";
    }
  };

  // Set photo as cover
  const handleSetAsCover = async (photoUrl: string) => {
    if (!selectedAlbum) return;
    
    try {
      const { error } = await supabase
        .from("photo_albums")
        .update({ cover_url: photoUrl })
        .eq("id", selectedAlbum.id);
      
      if (error) throw error;
      
      queryClient.invalidateQueries({ queryKey: ["admin-albums"] });
      setSelectedAlbum(prev => prev ? { ...prev, cover_url: photoUrl } : null);
      toast({ title: "Capa definida!" });
    } catch (error: any) {
      toast({ title: "Erro ao definir capa", description: error.message, variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-display text-primary">Galeria de Fotos</h2>
          <p className="text-muted-foreground">Gerencie os álbuns de fotos do terreiro</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Novo Álbum
        </Button>
      </div>

      {selectedAlbum ? (
        // Album Detail View
        <Card>
          <CardHeader className="flex flex-row items-start justify-between">
            <div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedAlbum(null)}
                className="mb-2"
              >
                ← Voltar aos álbuns
              </Button>
              <CardTitle className="flex items-center gap-2">
                {selectedAlbum.name}
                {selectedAlbum.is_published ? (
                  <Eye className="w-4 h-4 text-green-600" />
                ) : (
                  <EyeOff className="w-4 h-4 text-muted-foreground" />
                )}
              </CardTitle>
              {selectedAlbum.description && (
                <CardDescription>{selectedAlbum.description}</CardDescription>
              )}
            </div>
            <div className="flex items-center gap-2">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Excluir Álbum
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Excluir álbum?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Todas as fotos serão excluídas permanentemente. Esta ação não pode ser desfeita.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={() => deleteAlbumMutation.mutate(selectedAlbum.id)}>
                      Excluir
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Cover Section */}
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              <div className="w-40 h-28 rounded-lg overflow-hidden bg-muted">
                {selectedAlbum.cover_url ? (
                  <img src={selectedAlbum.cover_url} alt="Capa" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="w-8 h-8 text-muted-foreground" />
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Capa do Álbum</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleUploadCover}
                    disabled={uploadingCover}
                    className="max-w-[200px]"
                  />
                  {uploadingCover && <Loader2 className="w-4 h-4 animate-spin" />}
                </div>
              </div>
            </div>

            {/* Upload Photos */}
            <div className="space-y-2">
              <Label>Adicionar Fotos</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleUploadPhotos}
                  disabled={uploadingPhotos}
                />
                {uploadingPhotos && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Enviando...
                  </div>
                )}
              </div>
            </div>

            {/* Photos Grid */}
            {loadingPhotos ? (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="aspect-square bg-muted animate-pulse rounded-lg" />
                ))}
              </div>
            ) : photos && photos.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {photos.map((photo) => (
                  <div key={photo.id} className="relative group aspect-square rounded-lg overflow-hidden">
                    <img
                      src={photo.photo_url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <Button
                        size="icon"
                        variant="secondary"
                        className="h-8 w-8"
                        onClick={() => handleSetAsCover(photo.photo_url)}
                        title="Definir como capa"
                      >
                        <ImageIcon className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="destructive"
                        className="h-8 w-8"
                        onClick={() => deletePhotoMutation.mutate(photo)}
                        title="Excluir"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Images className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Nenhuma foto ainda. Use o botão acima para adicionar.</p>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        // Albums Grid
        <>
          {loadingAlbums ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(3)].map((_, i) => (
                <Card key={i}>
                  <div className="aspect-video bg-muted animate-pulse" />
                  <CardContent className="p-4">
                    <div className="h-5 bg-muted animate-pulse rounded mb-2" />
                    <div className="h-4 bg-muted animate-pulse rounded w-1/2" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : albums && albums.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {albums.map((album) => (
                <Card
                  key={album.id}
                  className="cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => setSelectedAlbum(album)}
                >
                  <div className="aspect-video relative overflow-hidden bg-muted">
                    {album.cover_url ? (
                      <img src={album.cover_url} alt={album.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Images className="w-12 h-12 text-muted-foreground/50" />
                      </div>
                    )}
                    <div className="absolute top-2 right-2">
                      <div
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          album.is_published
                            ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100"
                            : "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100"
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePublishMutation.mutate({ id: album.id, isPublished: !album.is_published });
                        }}
                      >
                        {album.is_published ? "Publicado" : "Rascunho"}
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-primary">{album.name}</h3>
                    {album.description && (
                      <p className="text-sm text-muted-foreground line-clamp-1 mt-1">{album.description}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-8 text-center">
              <Images className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="font-display text-xl text-primary mb-2">Nenhum álbum ainda</h3>
              <p className="text-muted-foreground mb-4">Crie seu primeiro álbum para começar a adicionar fotos.</p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Criar Álbum
              </Button>
            </Card>
          )}
        </>
      )}

      {/* Create Album Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo Álbum</DialogTitle>
            <DialogDescription>
              Crie um novo álbum para organizar suas fotos
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="album-name">Nome do Álbum *</Label>
              <Input
                id="album-name"
                value={newAlbumName}
                onChange={(e) => setNewAlbumName(e.target.value)}
                placeholder="Ex: Gira de Oxóssi - Dezembro 2024"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="album-description">Descrição (opcional)</Label>
              <Textarea
                id="album-description"
                value={newAlbumDescription}
                onChange={(e) => setNewAlbumDescription(e.target.value)}
                placeholder="Descreva o álbum..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => createAlbumMutation.mutate()}
              disabled={!newAlbumName.trim() || createAlbumMutation.isPending}
            >
              {createAlbumMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Criar Álbum
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};